import { User } from '../types';

// Log levels
type LogLevel = 'info' | 'warn' | 'error' | 'debug';

// Log entry structure
interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
}

// In-memory log storage
let logs: LogEntry[] = [];
let logCallback: ((entry: LogEntry) => void) | null = null;
let showLogsButton = import.meta.env.DEV;

// Maximum number of logs to keep in memory
const MAX_LOGS = 1000;

const debugLogsEnabled = () => {
  if (import.meta.env.DEV) return true;
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('forgedos_debug_logs') === 'true';
};

export const logger = {
  // Set a callback to be called whenever a new log is added
  setLogCallback: (callback: (entry: LogEntry) => void) => {
    logCallback = callback;
  },

  // Get all logs
  getLogs: () => [...logs],

  // Clear all logs
  clearLogs: () => {
    logs = [];
    if (logCallback) {
      logCallback({
        timestamp: new Date().toISOString(),
        level: 'info',
        message: 'Logs cleared'
      });
    }
  },

  // Toggle logs button visibility
  setShowLogsButton: (show: boolean) => {
    showLogsButton = show;
  },

  // Get logs button visibility state
  shouldShowLogsButton: () => showLogsButton,

  // Log an entry
  log: (level: LogLevel, message: string, data?: any) => {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data
    };

    if (debugLogsEnabled()) {
      const writer = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      writer(`[${entry.level.toUpperCase()}] ${entry.message}`, data || '');
    }
    
    logs.push(entry);
    if (logs.length > MAX_LOGS) {
      logs.shift();
    }

    if (logCallback) {
      logCallback(entry);
    }
  },

  // Convenience methods for different log levels
  info: (message: string, data?: any) => logger.log('info', message, data),
  warn: (message: string, data?: any) => logger.log('warn', message, data),
  error: (message: string, data?: any) => logger.log('error', message, data),
  debug: (message: string, data?: any) => logger.log('debug', message, data),

  // Auth-specific logging
  authEvent: (event: string, user: User | null, error?: any) => {
    logger.info(`Auth Event: ${event}`, { userId: user?.id, error });
  }
};
